import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

import Layout from 'components/Layout';

import { BookingPageComponent, CircularIndeterminate, DateFormattedUtils } from '@utriper/core-ui';
import { BookingStatus, BookingLog } from '@utriper/core';

import { useBooking } from 'core/contexts/booking';
import { usePayment } from 'core/contexts/payment';

import { ScheduleService } from 'core/api/services/schedule';
import { PreviewService } from 'core/api/services/preview';

import { useStyles } from './styles';
import { fetch, Method } from 'core/api/fetch';

const BookingPage = () => {
    const classes = useStyles();
    const { getBooking, booking, loading, setBooking, updateBooking, bookingLogs, getBookingLogs, setBookingLogs, totalBookingLogs, insuranceLogs, totalInsuranceLogs } = useBooking();
    const { getPayment, payment, setPayment } = usePayment();

    const [originalBooking, setOriginalBooking] = useState({});
    const [canEdit, setCanEdit] = useState(false);
    const [hasChanged, setHasChanged] = useState(false);
    const [logs, setLogs] = useState('bookingLogs');
    const [page, setPage] = useState(1);
    const MAX_PAGE = 2;
    const MAX_PAGE_INSURANCE = 10;

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    const locationParam = document.location.href.split('/');
    const bookingIndex = locationParam[locationParam.length - 1];

    useEffect(() => {
        setBooking({});
        if (bookingIndex == 0) {
            setCanEdit(true);
        } else {
            getBooking(bookingIndex);
        }
    }, []);

    useEffect(() => {
        if (!hasChanged) {
            setPayment({});
            if (booking.paymentId) {
                getPayment(booking.paymentId);
            }
            setBookingLogs([]);
            if (booking.id) {
                getBookingLogs(
                    booking.id,
                    {
                        pager: {
                            column: 'ci_created_on',
                            page,
                            pageSize: logs == 'bookingLogs' ? MAX_PAGE : MAX_PAGE_INSURANCE,
                        },
                    },
                    logs
                );
            }
        }
        // We do this to be able to cancel and revert unsaved changes
        if (!originalBooking.id || originalBooking.id !== booking.id) {
            const auxBooking = {
                ...booking,
            };
            setOriginalBooking(auxBooking);
        }
    }, [booking]);

    useEffect(() => {
        if (booking.id) {
            getBookingLogs(
                booking.id,
                {
                    pager: {
                        column: 'ci_created_on',
                        page,
                        pageSize: logs == 'bookingLogs' ? MAX_PAGE : MAX_PAGE_INSURANCE,
                    },
                },
                logs
            );
        }
    }, [page, logs]);

    const onSave = async (values) => {
        try {
            // values = { experiencer: userInfo };
            const valuesClon = { ...values };
            const res = await updateBooking(valuesClon);
            if (res) {
                toast.success('Booking updated!', {
                    position: 'top-right',
                    theme: 'colored',
                });
            }
            getBooking(bookingIndex);
        } catch (err) {
            toast.error(err, {
                position: 'top-right',
                theme: 'colored',
            });
        }
    };

    const onCancel = () => {
        setBooking(originalBooking);
    };

    const insuranceColumns = [
        { align: 'left', val: 'id', label: 'Id' },
        { align: 'left', val: 'formattedCreatedOn', label: 'Created On' },
        { align: 'left', val: 'formattedCreatedBy', label: 'Created By' },
        { align: 'center', val: 'policyId', label: 'Policy Id' },
        { align: 'center', val: 'policyStatus', label: 'Policy Status' },
        { align: 'center', val: 'insuranceProviderId', label: 'Insurance Provider Id' },
        { align: 'center', val: 'insuranceProductId', label: 'Insurance Product Id' },
        { align: 'center', val: 'method', label: 'Method' },
        { align: 'center', val: 'request', label: 'Request' },
        { align: 'center', val: 'responseStatus', label: 'Response Status' },
        { align: 'center', val: 'responseMessage', label: 'Response Message' },
    ];
    const columns = [
        { align: 'left', val: 'id', label: 'Id' },
        { align: 'left', val: 'formattedCreatedBy', label: 'Created By' },
        { val: 'formattedCreatedOn', label: 'Created On' },
        { align: 'center', val: 'formattedStatus', label: 'Status' },
        { align: 'center', val: 'formattedType', label: 'Type' },
        { align: 'left', val: 'formattedPreviousRecord', label: 'Previous' },
        { align: 'left', val: 'formattedNewRecord', label: 'After' },
        { align: 'center', val: 'additional', label: 'Additional' },
    ];

    // const prettifyLog = (log) => {
    //     const logParsed = JSON.parse(log);

    //     if (logParsed?.expDetail) {
    //         delete logParsed.expDetail;
    //     }
    //     if (logParsed?.resources) {
    //         delete logParsed.resources;
    //     }
    //     if (logParsed?.tripers) {
    //         delete logParsed.tripers;
    //     }
    //     if (logParsed?.bookingCommission) {
    //         delete logParsed.bookingCommission;
    //     }
    //     if (logParsed?.bookingCosts) {
    //         delete logParsed.bookingCosts;
    //     }

    //     return logParsed;
    // };

    const prettifyBookingLogs = (bookingLogs = []) => {
        return bookingLogs.map((log) => {
            let prevString = '';
            if (log.previousRecord) {
                prevString = renderLog(log.previousRecord);
            }
            let newString = '';
            if (log.newRecord) {
                newString = renderLog(log.newRecord);
            }
            return {
                formattedCreatedBy: parseInt(log.createdBy) === 0 ? 'system' : log.createdBy,
                formattedCreatedOn: DateFormattedUtils(log.createdOn),
                formattedType: BookingLog.getLabel(log.type),
                formattedStatus: BookingStatus.getLabel(log.status),
                formattedPreviousRecord: prevString,
                formattedNewRecord: newString,
                ...log,
            };
        });
    };

    const renderLogValue = (value) => {
        if (value === null) return 'null';

        if (typeof value !== 'object') {
            return String(value);
        }

        return (
            <ul>
                {Object.entries(value).map(([key, nestedValue]) => (
                    <li key={key}>
                        <strong>{key}:</strong> {renderLogValue(nestedValue)}
                    </li>
                ))}
            </ul>
        )
    };

    const renderLog = (log) => {
        if (!log) return null;

        try {
            const logParsed = JSON.parse(log);

            return renderLogValue(logParsed);
        } catch (error) {
            console.error('Failed to parse log record:', error);
            return null;
        }
    };

    const prettifyInsuranceLogs = (insuranceLogs = []) => {
        return insuranceLogs.map((log) => {
            return {
                formattedCreatedBy: parseInt(log.createdBy) === 0 ? 'system' : log.createdBy,
                formattedCreatedOn: DateFormattedUtils(log.createdOn),
                ...log,
            };
        });
    };

    const getList = async ({ entity, search, input}) => {
        try {
            let url;
            if (entity === 'experiencedetail') {
                url = `/api/v1.1.1/admin/search/${entity}?${search}=${input}&experienceId=${booking.experienceId}`;
            }
            else
            {
                url = `/api/v1.1.1/admin/search/${entity}?${search}=${input}`;
            }
           

            const response = await fetch(url, {
                method: Method.GET,
                authenticated: true,
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.data || !Array.isArray(response.data)) {
                return { data: [] };
            }

            return { data: response.data };
        } catch (error) {
            return { data: [] };
        }
    };


    return (
        <Layout contentClassName={classes.content}>
            {/* <Title value={'Booking'} /> */}
            <div>
                {loading ? (
                    <CircularIndeterminate centered={true} />
                ) : (
                    // STARTS BOOKING INFO
                    <BookingPageComponent
                        booking={booking}
                        setBooking={setBooking}
                        payment={payment}
                        bookingLogs={prettifyBookingLogs(bookingLogs)}
                        insuranceLogs={prettifyInsuranceLogs(insuranceLogs)}
                        bookingLogsOptions={{
                            columns,
                            page,
                            MAX_PAGE,
                            total: totalBookingLogs,
                            handlePageChange,
                        }}
                        insuranceLogsOptions={{
                            insuranceColumns,
                            page,
                            MAX_PAGE: MAX_PAGE_INSURANCE,
                            total: totalInsuranceLogs,
                            handlePageChange,
                        }}
                        showPayment={true}
                        logs={logs}
                        setLogs={setLogs}
                        showLogs={totalBookingLogs}
                        canEdit={true}
                        isEditable={true}
                        hasChanged={hasChanged}
                        setHasChanged={setHasChanged}
                        onSave={onSave}
                        onCancel={onCancel}
                        accionButtons={{
                            setDate: {
                                show: true,
                            },
                            setNoShow: {
                                show: true,
                            },
                            setCancel: {
                                show: true,
                            },
                        }}
                        getAvailability={ScheduleService.getAvailability}
                        previewService={PreviewService}
                        entityService={{getList}}
                        isUtools
                    />
                    // ENDS BOOKING INFO
                )}
            </div>
        </Layout>
    );
};

BookingPage.displayName = 'BookingPage';

export default BookingPage;
